// ===================================
// FILE UPLOAD COMPONENT
// src/components/FileUpload.js
// Reusable drag-and-drop file upload component
// ===================================

import React, { useState, useRef, useCallback } from 'react';
import './FileUpload.css';

const FileUpload = ({
  onFileSelect,
  accept = '.pdf,.doc,.docx,.txt',
  maxSize = 10 * 1024 * 1024, // 10MB default
  label = 'Upload File',
  description = 'Drag and drop or click to select',
  allowedTypes = ['PDF', 'DOC', 'DOCX', 'TXT'],
  disabled = false,
  loading = false,
  error = null,
  currentFile = null
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [localError, setLocalError] = useState(null);
  const fileInputRef = useRef(null);

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && !loading) {
      setIsDragging(true);
    }
  }, [disabled, loading]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const validateFile = (file) => {
    // Check file size
    if (file.size > maxSize) {
      return `File too large. Maximum size is ${Math.round(maxSize / 1024 / 1024)}MB`;
    }

    // Check file type
    const extension = file.name.split('.').pop().toLowerCase();
    const allowedExtensions = allowedTypes.map(t => t.toLowerCase());
    
    if (!allowedExtensions.includes(extension)) {
      return `Invalid file type. Allowed: ${allowedTypes.join(', ')}`;
    }

    return null;
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled || loading) return;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      const validationError = validateFile(file);
      
      if (validationError) {
        setLocalError(validationError);
        return;
      }

      setLocalError(null);
      onFileSelect(file);
    }
  }, [disabled, loading, maxSize, allowedTypes, onFileSelect]);

  const handleFileInput = useCallback((e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      const validationError = validateFile(file);
      
      if (validationError) {
        setLocalError(validationError);
        return;
      }

      setLocalError(null);
      onFileSelect(file);
    }
  }, [maxSize, allowedTypes, onFileSelect]);

  const handleClick = () => {
    if (!disabled && !loading) {
      fileInputRef.current?.click();
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1024 / 1024).toFixed(1) + ' MB';
  };

  const displayError = error || localError;

  return (
    <div className="file-upload-container">
      <div
        className={`file-upload-dropzone ${isDragging ? 'dragging' : ''} ${disabled ? 'disabled' : ''} ${loading ? 'loading' : ''} ${displayError ? 'has-error' : ''} ${currentFile ? 'has-file' : ''}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileInput}
          accept={accept}
          disabled={disabled || loading}
          style={{ display: 'none' }}
        />

        {loading ? (
          <div className="upload-loading">
            <div className="spinner"></div>
            <span>Uploading...</span>
          </div>
        ) : currentFile ? (
          <div className="upload-file-info">
            <span className="file-icon">📄</span>
            <div className="file-details">
              <span className="file-name">{currentFile.name || currentFile.original_name}</span>
              <span className="file-size">
                {formatFileSize(currentFile.size || currentFile.file_size)}
              </span>
            </div>
            <button
              className="change-file-btn"
              onClick={(e) => {
                e.stopPropagation();
                fileInputRef.current?.click();
              }}
            >
              Change
            </button>
          </div>
        ) : (
          <div className="upload-prompt">
            <span className="upload-icon">📤</span>
            <span className="upload-label">{label}</span>
            <span className="upload-description">{description}</span>
            <span className="upload-types">
              Accepted: {allowedTypes.join(', ')} (Max {Math.round(maxSize / 1024 / 1024)}MB)
            </span>
          </div>
        )}
      </div>

      {displayError && (
        <div className="upload-error">
          <span>⚠️</span> {displayError}
        </div>
      )}
    </div>
  );
};

export default FileUpload;
